import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, RefreshCw, ShieldCheck } from "lucide-react";
import {
  getObservabilityReadiness,
  ObservabilityReadinessProjection,
  RuntimeIdentityProjection,
} from "@/api/adminApi";
import { Button } from "@/components/ui/button";

type RuntimeTruthState =
  | { status: "loading"; readiness: null; error: null }
  | { status: "ready"; readiness: ObservabilityReadinessProjection; error: null }
  | { status: "error"; readiness: null; error: string };

const UNKNOWN = "Not available";

function display(value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined || value === "") return UNKNOWN;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

function workspaceLabel(identity: RuntimeIdentityProjection): string {
  return (
    identity.authoritative_environment ||
    identity.resolved_environment ||
    UNKNOWN
  ).toUpperCase();
}

function dataSetupLabel(identity: RuntimeIdentityProjection): string {
  if (identity.schema_current === true) return "Up to date";
  if (identity.schema_current === false) return "Needs update";
  return "Unable to confirm";
}

function isReady(
  readiness: ObservabilityReadinessProjection,
  identity: RuntimeIdentityProjection
): boolean {
  return (
    readiness.app_health_ready === true &&
    readiness.database_ready !== false &&
    readiness.redis_ready !== false &&
    identity.schema_current === true &&
    identity.settings_process_environment_match !== false
  );
}

function statusLabel(
  readiness: ObservabilityReadinessProjection,
  identity: RuntimeIdentityProjection
): string {
  if (isReady(readiness, identity)) return "Ready";
  if (identity.schema_current === null || identity.schema_current === undefined) {
    return "Check incomplete";
  }
  return "Needs attention";
}

function toneClasses(tone: "green" | "amber" | "red" | "blue" | "slate") {
  const tones = {
    green: "border-emerald-200 bg-emerald-50 text-emerald-900",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    red: "border-red-200 bg-red-50 text-red-900",
    blue: "border-blue-200 bg-blue-50 text-blue-950",
    slate: "border-gray-200 bg-white text-gray-700",
  };
  return tones[tone];
}

function statusTone(
  readiness: ObservabilityReadinessProjection | null,
  identity: RuntimeIdentityProjection | null,
  error: string | null
) {
  if (error) return "red";
  if (!readiness || !identity) return "slate";
  if (isReady(readiness, identity)) return "green";
  if (identity.schema_current === false || readiness.database_ready === false) {
    return "red";
  }
  return "amber";
}

function checkTone(isHealthy: boolean | null | undefined) {
  if (isHealthy === true) return "green";
  if (isHealthy === false) return "red";
  return "amber";
}

function checkLabel(isHealthy: boolean | null | undefined) {
  if (isHealthy === true) return "Ready";
  if (isHealthy === false) return "Needs attention";
  return "Unable to confirm";
}

function dataSetupTone(identity: RuntimeIdentityProjection) {
  if (identity.schema_current === true) return "green";
  if (identity.schema_current === false) return "red";
  return "amber";
}

function shortRelease(value: string | null | undefined): string {
  const shown = display(value);
  if (shown === UNKNOWN) return shown;
  return shown.length > 12 ? shown.slice(0, 12) : shown;
}

function useRuntimeTruth(): RuntimeTruthState & { refresh: () => Promise<void> } {
  const [state, setState] = useState<RuntimeTruthState>({
    status: "loading",
    readiness: null,
    error: null,
  });

  const refresh = useCallback(async () => {
    try {
      const readiness = await getObservabilityReadiness();
      setState({
        status: "ready",
        readiness,
        error: null,
      });
    } catch (error) {
      setState({
        status: "error",
        readiness: null,
        error:
          error instanceof Error
            ? error.message
            : "System status could not be checked.",
      });
    }
  }, []);

  useEffect(() => {
    void refresh();
    const interval = window.setInterval(() => void refresh(), 60_000);
    return () => window.clearInterval(interval);
  }, [refresh]);

  return { ...state, refresh };
}

function StatusBadge({
  tone,
  children,
}: {
  tone: "green" | "amber" | "red" | "blue" | "slate";
  children: React.ReactNode;
}) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${toneClasses(tone)}`}>
      {children}
    </span>
  );
}

function StatusTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: "green" | "amber" | "red" | "blue" | "slate";
}) {
  const Icon = tone === "red" || tone === "amber" ? AlertTriangle : CheckCircle2;

  return (
    <div className={`rounded-md border px-3 py-3 ${toneClasses(tone)}`}>
      <div className="flex items-center gap-2 text-sm font-semibold">
        <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span>{value}</span>
      </div>
      <div className="mt-1 text-xs font-medium uppercase text-current opacity-70">
        {label}
      </div>
    </div>
  );
}

function SupportField({
  label,
  value,
  danger,
}: {
  label: string;
  value: string | number | boolean | null | undefined;
  danger?: boolean;
}) {
  return (
    <div className="rounded-md border bg-white px-3 py-2">
      <div className="text-[11px] font-semibold uppercase text-gray-500">
        {label}
      </div>
      <div className={`mt-1 break-words text-sm font-semibold ${danger ? "text-red-700" : "text-gray-950"}`}>
        {display(value)}
      </div>
    </div>
  );
}

export function RuntimeTruthBanner() {
  const { status, readiness, error, refresh } = useRuntimeTruth();
  const identity = readiness?.runtime_identity ?? null;
  const tone = statusTone(readiness, identity, error);

  return (
    <section
      aria-label="System status"
      className={`mb-4 rounded-lg border px-3 py-2 text-xs shadow-sm ${toneClasses(tone)}`}
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="inline-flex items-center gap-2 font-semibold">
          <ShieldCheck className="h-4 w-4" aria-hidden="true" />
          System status
        </span>
        {status === "loading" ? <span>Checking status...</span> : null}
        {readiness && identity ? (
          <>
            <StatusBadge tone={tone}>{statusLabel(readiness, identity)}</StatusBadge>
            <span>
              Workspace: <strong>{workspaceLabel(identity)}</strong>
            </span>
            <span>
              Data setup: <strong>{dataSetupLabel(identity)}</strong>
            </span>
          </>
        ) : null}
        {error ? <span>Status check failed.</span> : null}
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="ml-auto h-7 gap-1 bg-white/70 px-2 text-xs"
          onClick={() => void refresh()}
          aria-label="Refresh system status"
        >
          <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
          Refresh
        </Button>
      </div>
    </section>
  );
}

export function RuntimeTruthCard() {
  const { status, readiness, error, refresh } = useRuntimeTruth();
  const identity = readiness?.runtime_identity ?? null;
  const overallTone = statusTone(readiness, identity, error);
  const schemaDanger = identity?.schema_current === false;
  const releaseLabel = identity?.git_commit ? shortRelease(identity.git_commit) : UNKNOWN;

  return (
    <section className="rounded-lg border bg-white p-4 shadow-sm" aria-label="System status">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-950">System status</h2>
          <p className="mt-1 text-sm text-gray-600">
            Quick readiness check for this workspace.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={() => void refresh()}
        >
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
          Refresh
        </Button>
      </div>

      {status === "loading" ? (
        <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
          Checking system status...
        </div>
      ) : null}

      {error ? (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          System status could not be checked.
        </div>
      ) : null}

      {readiness && identity ? (
        <>
          <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
            <StatusTile
              label="Overall"
              value={statusLabel(readiness, identity)}
              tone={overallTone}
            />
            <StatusTile
              label="Workspace"
              value={workspaceLabel(identity)}
              tone="blue"
            />
            <StatusTile
              label="Application"
              value={checkLabel(readiness.app_health_ready)}
              tone={checkTone(readiness.app_health_ready)}
            />
            <StatusTile
              label="Data setup"
              value={dataSetupLabel(identity)}
              tone={dataSetupTone(identity)}
            />
          </div>

          <details className="mt-3 rounded-md border border-gray-200 bg-gray-50">
            <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-gray-700">
              Support details
            </summary>
            <div className="grid gap-2 border-t border-gray-200 p-3 sm:grid-cols-2 xl:grid-cols-4">
              <SupportField label="Workspace" value={workspaceLabel(identity)} />
              <SupportField label="Sign-in service" value={checkLabel(readiness.redis_ready)} />
              <SupportField label="Database" value={identity.active_db_name} />
              <SupportField label="Configuration file" value={identity.env_file} />
              <SupportField label="Release" value={releaseLabel} />
              <SupportField
                label="Installed update"
                value={identity.alembic_current_revision}
                danger={schemaDanger}
              />
              <SupportField
                label="Expected update"
                value={identity.alembic_head_revision}
                danger={schemaDanger}
              />
              <SupportField
                label="Data setup"
                value={dataSetupLabel(identity)}
                danger={schemaDanger}
              />
            </div>
          </details>
        </>
      ) : null}
    </section>
  );
}
